import { Prose } from '../../components/Prose'
import { hasBabyPhase, type Profile } from '../../lib/metrics'

const HANDS: [string, string, string][] = [
  ['🤚', 'Protein — 1–2 avuç içi', 'Tavuk, et, balık, yumurta, peynir, baklagil. Avuç içinin kalınlığı kadar da kalın. Her öğünde olsun.'],
  ['✊', 'Sebze — 1–2 yumruk', 'Salata, haşlanmış/fırın sebze, çorbadaki sebze. Tabağın yarısı burası.'],
  ['🥄', 'Karbonhidrat — 1 çukur avuç', '≈4–5 yemek kaşığı pilav ya da 1 dilim ekmek. Kesmiyoruz, kısıyoruz.'],
  ['👍', 'Yağ — 1 başparmak', 'Zeytinyağı, tereyağı, kuruyemiş, tahin. Salataya "şöyle bir gezdirme" 200 kcal olabilir.'],
]

const RULES: [string, string][] = [
  ['Tabağı mutfakta doldur.', 'Tencereyi, sofra kabını masaya getirme. İkinci porsiyon için kalkmak zorunda kalırsan çoğu zaman kalkmazsın.'],
  ['22–24 cm tabak.', 'Aynı miktar büyük tabakta "az" görünür, küçük tabakta "dolu". Beyin gözle yer.'],
  ['Yemekten 10 dk önce 1 büyük bardak su.', 'Mide hacmini önden doldurur.'],
  ['Sıra: önce sebze/çorba → sonra protein → en son karbonhidrat.', 'Aynı yemek, farklı doygunluk.'],
  ['20 dakika kuralı.', 'Doygunluk sinyali mideden beyne ~20 dk\'da gider. Her 3–4 lokmada çatalı bırak.'],
  ['Ekran kapalı ye.', 'Kod yazarken yemek porsiyonu belirgin artırır ve doyduğunu fark etmezsin.'],
  ['Sipariş kuralı: gelir gelmez yarısını ayır.', 'Dolaba koy, yarın öğle yemeği. Ortada olmayan yemeği yemek imkânsız.'],
  ['Çerezi paketten yeme.', 'Kaseye koy, paketi kaldır. Paketten yerken porsiyon = paket boyu.'],
]

export function NutritionTab({ profile }: { profile: Profile }) {
  const m1 = profile.meal1.slice(0, 5)
  const m2 = profile.meal2.slice(0, 5)

  return (
    <>
      <div className="card" style={{ borderLeft: '3px solid var(--s2)' }}>
        <h2>Önce şunu anla: porsiyon sorunun bir irade sorunu değil</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 14.5, margin: '6px 0 0' }}>
          Günde 2 öğün yiyorsun: {m1} ve {m2}. Yani ilk öğüne <strong>yaklaşık 17 saat aç</strong> oturuyorsun.
          17 saat aç kalan hiç kimse küçük tabakla kalkmaz — bu fizyoloji, karakter zaafı değil.
          Çözüm "daha az ye" değil; <strong>öğünün ilk 5 dakikasını değiştirmek</strong>.
        </p>
        <p style={{ color: 'var(--ink-2)', fontSize: 14.5, margin: '10px 0 0' }}>
          İyi haber: {m1}–{m2} penceresi zaten iyi bir yapı, sabahları sıfır kalori alıyorsun ve hafta sonu
          düzenin bozulmuyor. İnsanların battığı üç yerde temizsin.
        </p>
      </div>

      <div className="card">
        <h2>İki tabak kuralı — 2 öğün yiyenler için tek numara</h2>
        <Prose className="card-h">Öğünü bir tabak değil <strong>iki ayrı tabak</strong> olarak kur. Sıra pazarlığa kapalı.</Prose>
        <div className="grid g2">
          <div className="day" style={{ borderLeft: '3px solid var(--s1)' }}>
            <div className="dn">Tabak 1 · ilk 5 dakika</div>
            <div className="dt">Çorba ya da büyük salata</div>
            <div className="dd">
              150–200 kcal. Ana tabağa <strong>bunu bitirmeden</strong> geçmek yok. Midenin üçte birini ucuz doldurur;
              20 dakika kuralının saati burada başlar. Ana yemeğe geldiğinde artık 17 saatlik aç adam değilsin.
            </div>
          </div>
          <div className="day">
            <div className="dn">Tabak 2 · asıl öğün</div>
            <div className="dt">2 avuç içi protein + 1 yumruk sebze + 1 çukur avuç karbonhidrat</div>
            <div className="dd">
              Mutfakta doldurulur, masaya tencere gelmez. Bu tabak bitince öğün biter — ikinci tur yok.
              Aynı yemeği yiyorsun, sadece sırası ve kabı değişti.
            </div>
          </div>
        </div>
        <div className="warnbox">
          Tek başına bu düzenek günde 400–600 kcal fark eder ve hiçbir şeyi yasaklamaz.
          Senin durumunda diğer bütün porsiyon tavsiyelerinden daha etkili, çünkü sorunun kaynağına dokunuyor.
        </div>
      </div>

      <div className="card">
        <h2>El ölçüsü</h2>
        <Prose className="card-h">Günde 2 öğün yediğin için her öğün protein açısından dolu olmak zorunda: <strong>öğün başı 2 avuç içi</strong>.</Prose>
        {HANDS.map(([icon, title, desc]) => (
          <div key={title} style={{ display: 'flex', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--grid)' }}>
            <div style={{ fontSize: 22, width: 30, textAlign: 'center', flex: 'none' }}>{icon}</div>
            <div>
              <strong>{title}</strong>
              <div style={{ color: 'var(--ink-2)', fontSize: 13.5 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>Porsiyonu küçülten 8 mekanik kural</h2>
        <Prose className="card-h">İrade değil, düzenek. Her biri kendi başına küçük; sekizi birlikte günde 400–600 kcal eder.</Prose>
        <ul className="tight">
          {RULES.map(([t, d]) => <li key={t}><strong>{t}</strong> {d}</li>)}
        </ul>
      </div>

      <div className="card">
        <h2>Öğün şablonları</h2>
        <div className="grid g2">
          <div className="day">
            <div className="dn">1. Öğün · {m1}</div><div className="dt">Günün büyük öğünü</div>
            <div className="dd">
              <em>Tabak 1:</em> Mercimek çorbası ya da büyük salata<br /><em>Tabak 2:</em>
              <ul className="tight">
                <li>Izgara tavuk/köfte (2 avuç içi) + bol salata + 4–5 kaşık bulgur</li>
                <li>Kuru fasulye/nohut (1 kepçe) + az pilav + cacık</li>
                <li>3 yumurtalı menemen + peynir + bol yeşillik + 1 dilim ekmek</li>
              </ul>
            </div>
          </div>
          <div className="day">
            <div className="dn">2. Öğün · {m2}</div><div className="dt">Sebze + protein ağırlıklı</div>
            <div className="dd">
              <em>Tabak 1:</em> Çorba ya da salata<br /><em>Tabak 2:</em>
              <ul className="tight">
                <li>Fırında tavuk/balık + fırın sebze + yoğurt</li>
                <li>Zeytinyağlı sebze yemeği + yoğurt + 3–4 kaşık bulgur</li>
                <li>Omlet (3 yumurta) + peynir + bol salata</li>
              </ul>
            </div>
          </div>
          <div className="day" style={{ borderLeft: '3px solid var(--s2)' }}>
            <div className="dn">Tenis günü</div><div className="dt">Öğünler kayar, sayı değişmez</div>
            <div className="dd">
              <ul className="tight">
                <li><strong>12:00</strong> — 1. öğün, biraz daha büyük</li>
                <li><strong>18:00</strong> — küçük ara (öğün değil), ~200 kcal</li>
                <li><strong>21:45</strong> — 2. öğün, protein ağırlıklı ve hafif</li>
              </ul>
              <span className="pill">Pencere bugün 12:00–22:00</span>
            </div>
          </div>
          <div className="day">
            <div className="dn">Hafta sonu</div><div className="dt">Değiştirme</div>
            <div className="dd">
              Aynı düzeni sürdürüyorsun — en büyük avantajın bu. Dışarıda yiyeceksen aynı iki tabak kuralı:
              önce çorba/salata iste, ana yemeği sonra. Tatlı yiyeceksen öğünün parçası olarak ye, ayrı üçüncü öğün olarak değil.
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Pazar meal-prep — 60 dakika</h2>
        <ul className="tight">
          <li>1.5 kg tavuk göğsü/but — 3 kaba böl</li>
          <li>Bir tencere bulgur pilavı — 4–5 porsiyon</li>
          <li>Sebzeleri doğra, kaplara koy</li>
          <li>Bir tencere mercimek çorbası — 4 öğün</li>
          <li>10 yumurta haşla — acil protein</li>
          <li>
            <strong>+2 porsiyon fazladan pişir ve dondur</strong> —{' '}
            {hasBabyPhase(profile) ? 'doğum sonrası stoğu' : 'yoğun günler için stok'}
          </li>
        </ul>
      </div>
    </>
  )
}
